import React, { useEffect, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { Grid } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
// import { useNavigate } from "react-router-dom";

import api from '../../utils/webcall';
import { getErrorMessage } from '../../utils/utils';
import AccessController from '../../components/access_controller';
import UserProfile from '../../components/User';

import { useCookies } from "react-cookie";

const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

const isAndroid = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android/.test(navigator.userAgent);
};

// helper: evaluate simple arithmetic expressions safely (basic sanitization)
function evaluateExpression(expr) {
    if (expr === null || expr === undefined) return '';
    const s = String(expr).trim();
    if (s === '') return '';
    // allow digits, whitespace, decimal point, + - * / and parentheses
    if (!/^[0-9+\-*/().\s]+$/.test(s)) {
        throw new Error('Invalid characters in expression');
    }
    // eslint-disable-next-line no-new-func
    const fn = Function('"use strict"; return (' + s + ')');
    const res = fn();
    if (typeof res === 'number' && !Number.isNaN(res) && Number.isFinite(res)) {
        if (res < 0) {
            throw new Error('Negative value not allowed');
        }
        return res;
    }
    throw new Error('Expression did not evaluate to a finite number. Please check your input.');
}

function InventoryFormRow(props) {
    const { data, index, update, cookieValue, errorMessage } = props;
    const initialEvaluated = (data && data['value'] !== undefined && data['value'] !== null) ? data['value'] : '';
    const initialFormula = cookieValue['formula'] || (data && data['formula']) || '';
    const [focused, setFocused] = useState(false);
    const [display, setDisplay] = useState(focused ? initialFormula : initialEvaluated);

    useEffect(() => {
        // sync when parent updates values/cookies
        if (!focused) {
            setDisplay(initialEvaluated);
        } else {
            setDisplay(initialFormula);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialEvaluated, initialFormula]);

    return (
        <TableRow
            key={`inventory-form-row-${index}`}
        >
            <TableCell align="center">{data['item_name']}</TableCell>
            <TableCell>
                <TextField
                    id={`outlined-adornment-weight-${index}`}
                    variant="outlined"
                    size="small"
                    error={!!errorMessage}
                    helperText={errorMessage || ''}
                    InputProps={{
                        endAdornment: <InputAdornment position="end">{data['measurement_unit']}</InputAdornment>,
                        inputMode: 'decimal'
                    }}
                    slotProps={{
                        htmlInput: {
                            'aria-label': `Quantity for ${data['item_name']} in ${data['measurement_unit']}`,
                            inputMode: isIOS() ? 'text' : 'tel',                       // hint for mobile keyboards (decimal keypad)
                            pattern: '[0-9]*([.,][0-9]+)?',             // allows integer or decimal input
                            'aria-label': 'quantity',
                            autoComplete: 'off',
                            style: { textAlign: 'right' }
                        }
                    }}
                    // inputProps={{
                    //     inputMode: 'decimal',                       // hint for mobile keyboards (decimal keypad)
                    //     pattern: '[0-9]*([.,][0-9]+)?',             // allows integer or decimal input
                    //     'aria-label': 'quantity'
                    // }}
                    onFocus={() => {
                        setFocused(true);
                        // show formula when focusing
                        setDisplay(initialFormula ?? '');
                    }}
                    onChange={(e) => {
                        const raw = e.target.value;
                        setDisplay(raw);
                        // notify parent of intermediate/raw formula (do not mark final evaluated yet)
                        update(index, raw);
                    }}
                    onBlur={(e) => {
                        setFocused(false);
                        const raw = e.target.value;
                        try {
                            const evaluated = evaluateExpression(raw);
                            // show evaluated result
                            setDisplay(evaluated);
                            update(index, raw, evaluated);
                        } catch (err) {
                            // invalid expression or not evaluable => keep raw visible
                            // use specific message from evaluator if present
                            update(index, raw, null, err && err.message ? err.message : 'Invalid expression');
                            setDisplay(raw);
                        }
                    }}
                    value={display}
                    fullWidth
                    type="text"
                />
            </TableCell>
        </TableRow>
    )
}

function InventoryForm(props) {
    const { data, update, cookie_data, errors } = props;

    if (data === null || data === undefined) {
        return <></>
    }
    if (data.length === 0) {
        return (
            <Paper
                elevation={2}
                sx={{
                    minHeight: 50,
                    padding: 10
                }}
            >
                No Inventory Pending for today.
            </Paper>
        )
    }
    return (
        <Paper
            elevation={4}
        >
            <TableContainer
                component={Paper}
                style={{
                    overflowX: "initial"
                }}
                key="inventory-table-container"
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">Item Name</TableCell>
                            <TableCell align="center">Quantity</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((item, index) => {
                            return (
                                <InventoryFormRow
                                    key={`inventory-form-${item['item_name']}`}
                                    data={item}
                                    index={index}
                                    update={update}
                                    cookieValue={(cookie_data && item['item_name'] in cookie_data) ? cookie_data[item['item_name']] : ""}
                                    errorMessage={(errors && errors[index]) ? errors[index] : null}
                                />
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}

function AddInventory(props) {
    const { showErrorMessage } = props;
    const cookie_name = 'divash-inventory-form'
    const [cookies, _cookie_setter] = useCookies([cookie_name]);
    const [submitButtonActive, setSubmitButtonActive] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const setCookies = (name, data, duration__in_hours = 1) => {
        const expirationDate = new Date();
        expirationDate.setHours(expirationDate.getHours() + duration__in_hours); // Set expiry to 1 hour ahead
        _cookie_setter(name, data, { path: '/', expires: expirationDate });
    }

    const [loading, setLoading] = useState(true);
    const [inventoryData, setInventoryData] = useState(null);
    const username = UserProfile.getName();
    // const navigate = useNavigate();

    useEffect(() => {
        if (cookies[cookie_name] == undefined) {
            setCookies(cookie_name, {});
        }
    }, [cookies])

    const updateInventoryData = (index, rawValue, evaluatedValue = null, explicitError = null) => {
        // clone
        let data = inventoryData ? [...inventoryData] : [];

        console.log("updating", index, rawValue, evaluatedValue, explicitError, data);
        if (!data[index]) return;
        // store raw formula
        data[index]['formula'] = rawValue;
        // determine numeric value
        if (evaluatedValue !== null && evaluatedValue !== undefined && evaluatedValue !== '') {
            data[index]['value'] = evaluatedValue;
        } else {
            // try to evaluate rawValue if possible
            try {
                data[index]['value'] = evaluateExpression(rawValue);;
            } catch (err) {
                // leave existing value as-is if not evaluable
            }
        }
        console.log("updated data", data[index], data);
        setInventoryData(data);

        // update cookie map
        let cookieMap = cookies[cookie_name] || {};
        if (rawValue === '' || rawValue == null) {
            if (data[index] && data[index]['item_name'] in cookieMap) {
                delete cookieMap[data[index]['item_name']];
            }
        } else {
            cookieMap[data[index]['item_name']] = {
                'formula': data[index]['formula'],
                'value': data[index]['value'],
                'measurement_unit': data[index]['measurement_unit'],
                'id': data[index]['id']
            };
        }
        console.log("updated cookieMap", cookieMap);
        setCookies(cookie_name, cookieMap);

        // update errors map
        setErrors(prev => {
            const next = { ...(prev || {}) };
            // explicitError provided by caller (e.g., onBlur)
            if (explicitError) {
                next[index] = explicitError;
            } else {
                // determine if rawValue is invalid: non-empty and not evaluable OR negative result
                if (rawValue && rawValue.toString().trim() !== '') {
                    try {
                        // evaluateExpression will throw for invalid chars or negative values
                        evaluateExpression(rawValue);
                        delete next[index];
                    } catch (err) {
                        next[index] = err && err.message ? err.message : 'Invalid expression';
                    }
                } else {
                    delete next[index];
                }
            }
            // remove keys with undefined/null to keep object clean
            Object.keys(next).forEach(k => { if (!next[k]) delete next[k]; });
            return next;
        });
    }

    // when errors become empty ensure submit button becomes active again
    useEffect(() => {
        const hasErrors = errors && Object.keys(errors).length > 0;
        if (!hasErrors && !submitButtonActive) {
            setSubmitButtonActive(true);
        }
    }, [errors, submitButtonActive]);

    const show = () => {
        return (!(inventoryData === null || inventoryData === undefined) && inventoryData.length > 0);
    }

    const loadInventoryToBeDone = () => {
        setLoading(true);
        api.get(`/pos/inventory/list/non_perishable/`,
        ).then((res) => {
            if (res.status === 200) {
                let data = res.data['inventory_items'];
                data.sort(function (a, b) {
                    if (a['display_index'] < b['display_index']) return -1;
                    return 1;
                });

                // apply cookie-stored formulas (if any) and compute evaluated values
                const cookieMap = cookies[cookie_name] || {};
                data = data.map((item, idx) => {
                    const copied = { ...item };
                    if (cookieMap && (copied['item_name'] in cookieMap)) {
                        const raw = cookieMap[copied['item_name']]['formula'];
                        copied['formula'] = raw;
                        try {
                            copied['value'] = evaluateExpression(raw);
                            // clear any previous error for this row
                            setErrors(prev => {
                                const next = { ...(prev || {}) };
                                delete next[idx];
                                return next;
                            });
                        } catch (err) {
                            // mark initial error for this row with specific message
                            setErrors(prev => ({ ...(prev || {}), [idx]: (err && err.message) ? err.message : 'Invalid expression' }));
                        }
                    }
                    return copied;
                });

                setInventoryData(data);
                setLoading(false);
            }
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        }).finally(() => {
            setLoading(false);
        });
    }

    useEffect(() => {
        // if(!loading){
        loadInventoryToBeDone();
        // }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <AccessController
            onlyAdmin={false}
        >
            <Grid
                container
                direction={'column'}
                rowSpacing={2}
                alignItems="center"
            >
                <Grid>
                    <i>Welcome {username}</i>
                </Grid>
                <Grid>
                    <i>Inventory to be done Today</i>
                </Grid>

                {loading ? <Grid><CircularProgress /></Grid> : <></>}
                {!loading ? <InventoryForm data={inventoryData} update={updateInventoryData} cookie_data={cookies[cookie_name]} errors={errors} /> : <></>}
                <Grid>
                    {show() ? (() => {
                        const hasErrors = errors && Object.keys(errors).length > 0;
                        return (
                            <Button
                                variant="contained"
                                disabled={isSubmitting || !submitButtonActive || hasErrors}
                                onClick={async (e) => {
                                    e.preventDefault();
                                    if (isSubmitting) return;
                                    setIsSubmitting(true);
                                    setSubmitButtonActive(false);
                                    try {
                                        // validate/evaluate all formulas before sending
                                        const newErrors = {};
                                        const payload = (inventoryData || []).map((it, idx) => {
                                            const copy = { ...it };
                                            const raw = copy['formula'];

                                            // if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
                                            //     try {
                                            //         copy['value'] = evaluateExpression(raw);
                                            //     } catch (err) {
                                            //         newErrors[idx] = err && err.message ? err.message : 'Invalid expression';
                                            //     }
                                            // } else if (copy['value'] === undefined || copy['value'] === null || copy['value'] === '') {
                                            //     // if empty and not evaluable, allow server-side validation — keep as-is
                                            // }
                                            if (idx == 35) {
                                                console.log("copy", copy);
                                            }
                                            return ('value' in copy && copy['value'] !== undefined && copy['value'] !== null && String(copy['value']).trim() !== '') ? {
                                                'id': copy['id'],
                                                'value': copy['value'],
                                                'measurement_unit': copy['measurement_unit']
                                            } : {
                                                'id': copy['id'],
                                            }
                                        });
                                        console.log("Final payload", payload, inventoryData);
                                        if (Object.keys(newErrors).length > 0) {
                                            // set errors and abort submit
                                            setErrors(newErrors);
                                            showErrorMessage('Fix invalid inputs before sending.');
                                            setSubmitButtonActive(true);
                                            return;
                                        }

                                        const res = await api.post(`/pos/inventory/update/`, payload);
                                        if (res.status === 200) {
                                            setCookies(cookie_name, {})
                                            loadInventoryToBeDone();
                                            setErrors({});
                                        }
                                    } catch (error) {
                                        showErrorMessage(getErrorMessage(error));
                                    } finally {
                                        setIsSubmitting(false);
                                        setSubmitButtonActive(true);
                                    }
                                }}
                            >
                                {isSubmitting ? 'Sending...' : 'Send'}
                            </Button>
                        );
                    })() : <></>}
                </Grid>
            </Grid>
        </AccessController>
    )
}

export default AddInventory;